import { useUserUpdatesMutation } from "@codegen/index";
import { UpdateUserByIdInput } from "@codegen/schema";
import {
  FormContainer,
  OnboardingContainer,
  OnboardingFormField,
} from "@components/onboarding";
import { useReadOnlyMode } from "@components/readOnlyModeProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { OnboardingPageType } from "@pages/onboarding/types";
import { useDebounceCallback } from "@react-hook/debounce";
import { useLogError } from "@utils/error";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { z } from "zod";

export const legalNameFormSchema = z.object({
  firstName: z.string().min(1, "Please provide a first name"),
  lastName: z.string().min(1, "Please provide a last name"),
  middleName: z.string().optional(),
});

export type LegalNameFormType = z.infer<typeof legalNameFormSchema>;

export const LegalNameForm: React.FC<OnboardingPageType> = ({
  contact,
  onSubmit,
  onBack,
  autoSave,
  exhibit,
  onSkipForNow,
  moduleProgress,
  currentStep,
  showSkipForNow,
  hideExitButton,
}) => {
  const {
    data,
  }: {
    data: {
      firstName?: string;
      lastName?: string;
      middleName?: string;
    };
  } = exhibit;

  const { isReadOnly } = useReadOnlyMode();
  const [status, setStatus] = useState<
    "idle" | "loading" | "error" | "success"
  >("idle");

  // Track pending save operations to avoid race conditions
  const pendingSaveRef = useRef<Promise<void> | null>(null);

  const methods = useForm<LegalNameFormType>({
    defaultValues: {
      firstName: data.firstName ?? contact.firstName ?? "",
      lastName: data.lastName ?? contact.lastName ?? "",
      middleName: data.middleName ?? contact.middleName ?? "",
    },
    resolver: zodResolver(legalNameFormSchema),
  });
  const { control, setError } = methods;
  const logError = useLogError();
  const [userUpdatesMutation, { loading: saving }] = useUserUpdatesMutation();

  const watchedValues = useWatch({
    control,
  });

  const performMutation = useCallback(
    async (data: LegalNameFormType) => {
      const { errors: updateUserError } = await userUpdatesMutation({
        variables: {
          input: {
            id: contact.id,
            userPatch: {
              fullName: data.middleName
                ? `${data.firstName} ${data.middleName} ${data.lastName}`
                : `${data.firstName} ${data.lastName}`,
            },
          } as UpdateUserByIdInput,
        },
      });

      if (updateUserError != null) {
        throw updateUserError[0];
      }
    },
    [userUpdatesMutation, contact.id]
  );

  const handleSave = useCallback(
    async (data: LegalNameFormType) => {
      const savePromise = (async () => {
        try {
          setStatus("loading");
          await performMutation(data);
          setStatus("success");
          autoSave(data);
        } catch (exception) {
          setStatus("error");
          if (exception instanceof Error) {
            setError("root.serverError", {
              type: exception.name,
              message: exception.message,
            });
          }
          logError(exception, {
            message: "Error saving contact",
            data,
          });
        } finally {
          pendingSaveRef.current = null;
        }
      })();

      pendingSaveRef.current = savePromise;
      await savePromise;
    },
    [performMutation, autoSave, setError, logError]
  );

  const debouncedSave = useDebounceCallback(handleSave, 300);

  useEffect(() => {
    // Only auto-save if we have valid data
    if (watchedValues.firstName && watchedValues.lastName) {
      debouncedSave(watchedValues as LegalNameFormType);
    }
  }, [watchedValues, debouncedSave]);

  const handleSubmit = useCallback(
    async (data: LegalNameFormType) => {
      try {
        // Wait for any pending auto-save to complete
        if (pendingSaveRef.current) {
          await pendingSaveRef.current;
        }

        setStatus("loading");
        await performMutation(data);
        setStatus("success");
        onSubmit(data);
      } catch (exception) {
        setStatus("error");
        if (exception instanceof Error) {
          setError("root.serverError", {
            type: exception.name,
            message: exception.message,
          });
        }
        logError(exception, {
          message: "Error saving contact",
          data,
        });
      }
    },
    [performMutation, onSubmit, setError, logError]
  );

  return (
    <FormProvider {...methods}>
      <OnboardingContainer
        title={exhibit.name}
        subtitle={`Hello ${contact?.firstName}! We're excited to get started on your case.`}
        cardTitle="Could you please share your full legal name?"
        progress={moduleProgress}
        onSubmit={methods.handleSubmit(handleSubmit)}
        exhibit={exhibit}
        name={`legal-name-form`}
        onBack={onBack}
        onSkipForNow={onSkipForNow}
        isLoading={saving}
        currentStep={currentStep}
        showSkipForNow={showSkipForNow}
        hideExitButton={hideExitButton}
      >
        <FormContainer
          status={status}
          errorMessage="There was a problem saving your name. Please try again."
        >
          <OnboardingFormField
            name="firstName"
            label="First Name (Given Name)"
            placeholder="First Name"
            required={true}
            disabled={isReadOnly}
          />

          <OnboardingFormField
            name="middleName"
            label="Middle Name"
            description="Optional"
            placeholder="Middle Name"
            disabled={isReadOnly}
          />

          <OnboardingFormField
            name="lastName"
            label="Last Name (Family Name)"
            placeholder="Last Name"
            required={true}
            disabled={isReadOnly}
          />
        </FormContainer>
      </OnboardingContainer>
    </FormProvider>
  );
};

export default LegalNameForm;
